import { Mutex, MutexInterface } from 'async-mutex';

import { logger } from '../../datadog/logger';

/** Adds a final `name: string` param to the given methods for logging. */
type WithNamedCalls<T, K extends keyof T> = Omit<T, K> & {
  [P in K]: T[P] extends (...args: infer A) => infer R ? (...args: [...A, string]) => R : T[P];
};
type DebugMutexInterface = WithNamedCalls<MutexInterface, 'runExclusive' | 'acquire'>;

type Waiter = { name: string; acquiredAt: number };

export class DebugMutex implements DebugMutexInterface {
  private name: string;
  private mutex = new Mutex();
  private waiters: Waiter[] = [];

  constructor(name: string) {
    this.name = name;
  }

  private removeWaiter(name: string): Waiter | undefined {
    const idx = this.waiters.findIndex((w) => w.name === name);
    if (idx === -1) return undefined;
    const [w] = this.waiters.splice(idx, 1);
    return w;
  }

  async acquire(name: string): Promise<MutexInterface.Releaser> {
    this.waiters.push({ name, acquiredAt: Date.now() });
    this.log(`acquire(${name})`);

    const releaser = await this.mutex.acquire();

    return () => {
      this.log(`releasing(${name})`);
      this.removeWaiter(name);
      return releaser();
    };
  }

  waitForUnlock(): Promise<void> {
    return this.mutex.waitForUnlock();
  }

  isLocked(): boolean {
    return this.mutex.isLocked();
  }

  release(): void {
    return this.mutex.release();
  }

  cancel(): void {
    this.log(`cancel()`);
    this.waiters = [];
    return this.mutex.cancel();
  }

  async runExclusive<T>(callback: MutexInterface.Worker<T>, name: string): Promise<T> {
    this.waiters.push({ name, acquiredAt: Date.now() });
    this.log(`runExclusive(${name}): acquiring`);

    try {
      return await this.mutex.runExclusive(callback);
    } finally {
      const w = this.removeWaiter(name);
      const heldForMs = w ? Date.now() - w.acquiredAt : undefined;
      this.log(`runExclusive(${name}): releasing`, { heldForMs });
    }
  }

  log(message: string, context: Record<string, unknown> = {}) {
    const first = this.waiters[0];
    logger.info(`[Mutex] ${this.name} ${message}`, {
      currentHolder: first?.name ?? null,
      acquiredAt: first?.acquiredAt ?? null,
      now: Date.now(),
      waiters: [...this.waiters],
      isLocked: this.mutex.isLocked(),
      ...context,
    });
  }
}

/**
 * A mutex to serialize core video SDK operations like audio/video
 * initialization, media element attachment/detachment, joining/leaving rooms,
 * etc. across parts of the application that run concurrently e.g. operations
 * that run reactively and user-initiated operations.
 */
export const videoSDKMutex = new DebugMutex('videoSDKMutex');
